import React, { useRef, useState } from 'react';
import TypeAhead, { type TypeAheadOption } from '../../../components/TypeAhead';
import quoteService from '../../../services/quote.service';
import type { LocationLookupRow } from '../fixtures';

interface Props {
  /** Current display name (free text). */
  value: string;
  /** User typed (or picked) a name — caller stores it. */
  onType: (name: string) => void;
  /** User picked a cms.locations result — caller fills the denormalized address. */
  onPick: (row: LocationLookupRow) => void;
  ariaLabel?: string;
  placeholder?: string;
  className?: string;
  /** Reduced padding/font for dense inline rows (default true). */
  compact?: boolean;
}

/**
 * cms.locations type-ahead built on the shared <TypeAhead> in free-text + async
 * mode. Typing 2+ chars debounce-searches locations (quoteService.lookupLocation);
 * picking a result fills the denormalized address via onPick. Free text is
 * allowed (no location code is linked) — used for stopoff names.
 */
const LocationTypeAhead: React.FC<Props> = ({
  value, onType, onPick, ariaLabel, placeholder, className, compact = true,
}) => {
  const [options, setOptions] = useState<TypeAheadOption[]>([]);
  const timer = useRef<number | undefined>(undefined);

  const search = (term: string) => {
    if (timer.current) window.clearTimeout(timer.current);
    const q = term.trim();
    if (q.length < 2) { setOptions([]); return; }
    timer.current = window.setTimeout(async () => {
      try {
        const rows = await quoteService.lookupLocation(q);
        setOptions(rows.map((r) => ({
          value: String(r.locationcode),
          label: r.lname,
          description: [
            [r.city, r.state].filter(Boolean).join(', '),
            `#${r.locationcode}`,
            r.cyalias,
            r.locationtype,
          ].filter(Boolean).join(' · '),
          data: r,
        })));
      } catch {
        setOptions([]);
      }
    }, 300);
  };

  return (
    <TypeAhead
      freeText
      multiple={false}
      value={value}
      options={options}
      onChange={onType}
      onSearch={search}
      onSelect={(opt) => { if (opt.data) onPick(opt.data as LocationLookupRow); }}
      ariaLabel={ariaLabel}
      placeholder={placeholder ?? 'Search locations — min 2 characters…'}
      className={className}
      compact={compact}
      showDescriptions
    />
  );
};

export default LocationTypeAhead;
